import { useState } from 'react'

import { api } from '../api/client'
import type { FleetVersions as VersioniFlotta } from '../api/types'
import { Card, ErrorBanner, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from './DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Quale versione hanno le sonde, e come portarle avanti.
 *
 * Aggiornare una sonda si poteva già fare, ma andava lanciato a mano in SSH su
 * ogni macchina. Con una fleet sparsa nelle reti di clienti diversi è la parte
 * del ciclo di vita che non si fa — e le sonde restano indietro finché qualcosa
 * smette di funzionare.
 *
 * Il pulsante avvia un rollout **a lotti**, non un aggiornamento di tutto. Un
 * binario difettoso mandato a una sonda è un disservizio; mandato a quaranta
 * sonde nello stesso istante è un giro di visite in sede, perché quelle macchine
 * stanno dentro reti a cui non si accede da remoto. La schermata lo dice, perché
 * altrimenti «ne sono partite due su quaranta» si legge come un guasto.
 */

type SondaIndietro = VersioniFlotta['outdated'][number]

/** The columns of the probes left behind (a function: t() follows the current language). */
const colonne = (): Column<SondaIndietro>[] => [
  { key: 'name', header: t('Sonda') },
  { key: 'version', header: t('Versione'), mono: true },
  {
    key: 'state',
    header: t('Stato'),
    value: (p) => (p.state === 'failed' ? t('fallita') : t('indietro')),
    render: (p) => (p.state === 'failed' ? <span className="badge--warn">{t('fallita')}</span> : t('indietro')),
  },
  { key: 'error', header: t('Motivo'), render: (p) => p.error ?? '' },
]

export default function FleetVersions() {
  const versions = useAsync(() => api.fleetVersions(), [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  async function rollout() {
    setBusy(true)
    setError(null)
    setOutcome(null)
    try {
      const result = await api.rolloutFleet(2)
      setOutcome(
        result.started.length === 0
          ? result.already_running > 0
            ? t('Nessuna sonda avviata: ce ne sono già {n} in aggiornamento. Il lotto successivo parte quando queste tornano.', { n: result.already_running })
            : t('Nessuna sonda da aggiornare.')
          : result.queued > 0
            ? t('Avviate {n} sonde ({elenco}). Altre {coda} in coda: partiranno man mano che queste tornano con la versione nuova.', {
                n: result.started.length,
                elenco: result.started.join(', '),
                coda: result.queued,
              })
            : t('Avviate {n} sonde ({elenco}).', {
                n: result.started.length,
                elenco: result.started.join(', '),
              }),
      )
      versions.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (versions.loading && !versions.data) return <Spinner />

  const data = versions.data
  if (!data) return <ErrorBanner error={versions.error} />

  const { counts } = data
  const canRollout = data.available !== null && counts.outdated > 0

  return (
    <Card
      title={t('Versione delle sonde')}
      actions={
        <button onClick={rollout} disabled={busy || !canRollout}>
          {busy ? t('Avvio…') : t('Aggiorna le sonde indietro')}
        </button>
      }
    >
      <ErrorBanner error={versions.error ?? error} />

      {data.available === null ? (
        // Dire «tutte da aggiornare» perché non si conosce la versione manderebbe
        // la fleet a scaricare un binario identico a quello che ha già.
        <div className="banner banner--warn">
          <strong>{t('Questo Control Plane non conosce la propria versione.')}</strong>{' '}
          {t('È stato costruito senza {variabile}, quindi non c’è nulla con cui confrontare le sonde e nessun aggiornamento può essere richiesto.', { variabile: 'CENSO_BUILD_SHA' })}
        </div>
      ) : (
        <p style={{ marginTop: 0 }}>
          {t('Versione distribuita da questa installazione:')}{' '}
          <span className="mono">{data.available}</span>
        </p>
      )}

      <div className="stats">
        <StatCard label={t('Aggiornate')} value={counts.current ?? 0} />
        <StatCard
          label={t('Indietro')}
          value={counts.outdated ?? 0}
          tone={counts.outdated ? 'warn' : undefined}
        />
        <StatCard label={t('In aggiornamento')} value={counts.updating ?? 0} />
        <StatCard
          label={t('Fallite')}
          value={counts.failed ?? 0}
          tone={counts.failed ? 'error' : undefined}
          hint={counts.failed ? t('non si riprova da sé') : undefined}
        />
        <StatCard
          label={t('Sconosciute')}
          value={counts.unknown ?? 0}
          hint={t('non hanno ancora dichiarato una versione')}
        />
      </div>

      {data.in_progress && (
        // Senza questo, chi apre la pagina e vede sonde ancora indietro ne avvia
        // un secondo rollout sopra il primo.
        <div className="banner banner--info">
          {t('Aggiornamento in corso: le sonde vengono aggiornate gradualmente in funzione del gruppo di appartenenza. Tra un gruppo e il successivo possono trascorrere alcuni minuti.')}
        </div>
      )}

      {counts.failed > 0 && (
        <div className="banner banner--warn">
          <strong>{t('Alcune sonde non hanno completato l’aggiornamento.')}</strong>{' '}
          {t('Per evitare tentativi ripetuti senza esito, dopo due fallimenti il sistema sospende l’aggiornamento. Se una sonda completa l’operazione in seguito, il suo stato viene aggiornato automaticamente.')}
        </div>
      )}

      {outcome && <div className="banner banner--ok">{outcome}</div>}

      {data.outdated.length > 0 && (
        <DataTable rows={data.outdated} columns={colonne()} rowKey={(p) => p.id} conteggio={{ nome: t('sonde') }} />
      )}

      <p className="field__hint">
        {t('L’aggiornamento potrebbe non iniziare subito. Le sonde verificano periodicamente la disponibilità della nuova versione. Se l’aggiornamento impedisce alla sonda di ripartire correttamente, viene ripristinata automaticamente la versione precedente.')}
      </p>
    </Card>
  )
}
